import type { KeyboardEvent } from 'react';

import { cn } from '@poseidon/components/cn';
import { isNotEmptyTipTap } from '@poseidon/components/utils';

import { stepEditSpec, type SequenceStep } from '~/components/Sequence/sequenceSteps';

/** One run of the preview text: a plain-text run, or a variable/mention chip's text (rendered bold). */
interface PreviewSegment {
  text: string;
  isVar: boolean;
}

/** A variable / document / comment mention in TipTap HTML is any element carrying `data-id` (the same
 *  marker `convertTemplateToHtml` keys on). We capture its inner text so it can be bolded in the preview. */
const MENTION_RE = /<[a-zA-Z][^>]*\bdata-id=[^>]*>([\s\S]*?)<\/[a-zA-Z][^>]*>/g;
const MAX_PREVIEW_CHARS = 240;

/**
 * SSR-safe, unicode-preserving strip of a TipTap HTML fragment to plain text. We deliberately DON'T reuse
 * the shared `getTextFromHtml` here: it runs `DOMParser` (browser-only, so it can't run during the page's
 * server render) and strips every non-ASCII char (`[^\x20-\x7E]`), which would mangle names/emoji in a
 * preview. This regex strip keeps unicode and works on both the server and client passes.
 */
function htmlToText(html: string): string {
  return html
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, ' ');
}

/**
 * Split a step's field HTML (subject/body) into plain-text runs and variable-mention runs, so the preview
 * can render the mentions **bold** while the surrounding copy stays regular. Block/`<br>` boundaries become
 * spaces first (so "line one</p><p>line two" reads as "line one line two"); the total is capped so an
 * enormous body doesn't bloat the DOM (the line-clamp then trims what actually shows). Returns `[]` for an
 * empty field. Pure + SSR-safe (regex only, no DOMParser) — see {@link htmlToText}.
 */
export function fieldSegments(html: string | undefined): PreviewSegment[] {
  if (!isNotEmptyTipTap(html)) return [];
  const src = (html ?? '').replace(/<br\s*\/?>/gi, ' ').replace(/<\/(p|div|li|h[1-6]|blockquote)>/gi, ' ');

  const runs: PreviewSegment[] = [];
  const re = new RegExp(MENTION_RE); // fresh lastIndex per call
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(src)) != null) {
    if (m.index > last) runs.push({ text: htmlToText(src.slice(last, m.index)), isVar: false });
    runs.push({ text: htmlToText(m[1] ?? ''), isVar: true });
    last = re.lastIndex;
  }
  if (last < src.length) runs.push({ text: htmlToText(src.slice(last)), isVar: false });

  // Drop empties and cap the total length (truncating the run that crosses the cap).
  const segments: PreviewSegment[] = [];
  let used = 0;
  for (const run of runs) {
    if (run.text === '') continue;
    if (used >= MAX_PREVIEW_CHARS) break;
    const room = MAX_PREVIEW_CHARS - used;
    if (run.text.length > room) {
      segments.push({ text: `${run.text.slice(0, room).trimEnd()}…`, isVar: run.isVar });
      break;
    }
    segments.push(run);
    used += run.text.length;
  }
  // Trim leading/trailing whitespace at the very edges of the preview only.
  if (segments.length > 0) {
    const first = segments[0]!;
    segments[0] = { ...first, text: first.text.replace(/^\s+/, '') };
    const last2 = segments[segments.length - 1]!;
    segments[segments.length - 1] = { ...last2, text: last2.text.replace(/\s+$/, '') };
  }
  return segments.filter((s) => s.text !== '');
}

/**
 * Whether a step has authored content worth previewing beside it — an editable content step (message /
 * connect note / inmail / email / call note / reminder note) with a non-empty subject or body. `like`
 * (a count, no text) and the non-content steps (follow / wait / await / containers) have nothing to show.
 * The single source of truth the graph uses to decide whether to render a preview + reserve room for it.
 */
export function stepHasContentPreview(step: SequenceStep): boolean {
  return stepEditSpec(step.type) != null && (isNotEmptyTipTap(step.subject) || isNotEmptyTipTap(step.body));
}

/** One preview line (subject or body): the field's text with variable/mention chips rendered bold. */
function PreviewLine({ segments, className }: { segments: PreviewSegment[]; className?: string }) {
  return (
    <div className={className}>
      {segments.map((seg, i) =>
        seg.isVar ? (
          <strong key={i} className="font-black">
            {seg.text}
          </strong>
        ) : (
          <span key={i}>{seg.text}</span>
        ),
      )}
    </div>
  );
}

interface Props {
  step: SequenceStep;
  /**
   * Open this step's content editor (the card is a large click target for editing, like clicking the node).
   * Omit → a STATIC, non-interactive preview (the locked per-contact graph passes this only for editable,
   * not-yet-sent steps, so a sent step's preview isn't a dead "Edit step content" click target).
   */
  onEdit?: () => void;
  /** Cap the card width (px) when branch lanes claim the right gutter, so the preview never overlaps them. */
  maxWidthPx?: number;
}

/**
 * A compact, read-at-a-glance preview of a content step's authored text, shown in the gutter to the RIGHT
 * of its node on the sequence graph (the people-piles sit on the left). Email / InMail lead with the bold
 * subject; every content step shows a body snippet. Variable mentions (`@` chips) render **bold** so they
 * stand out from the surrounding copy. Clicking the card opens that step's editor (when `onEdit` is
 * provided; otherwise it's static text). Renders nothing when the step has no content — see
 * {@link stepHasContentPreview}.
 */
export function SequenceStepPreview({ step, onEdit, maxWidthPx }: Props) {
  const subject = fieldSegments(step.subject);
  const body = fieldSegments(step.body);
  if (subject.length === 0 && body.length === 0) return null;

  const content = (
    <>
      {subject.length > 0 && <PreviewLine segments={subject} className="line-clamp-1 font-medium" />}
      {body.length > 0 && (
        <PreviewLine segments={body} className={cn('leading-snug', subject.length === 0 ? 'line-clamp-3' : 'line-clamp-2')} />
      )}
    </>
  );

  // Static (non-editable) preview — plain faint text, no button affordance.
  if (onEdit == null) {
    return (
      <div style={{ maxWidth: maxWidthPx }} className="w-56 text-left text-sm text-lynch-400 dark:text-lynch-500">
        {content}
      </div>
    );
  }

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onEdit();
    }
  };

  // No card / border / background — the text sits faint directly on the canvas and darkens on hover; the
  // node itself is the visual anchor. Color is inherited from the wrapper so hover moves the whole block.
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onEdit}
      onKeyDown={onKeyDown}
      aria-label="Edit step content"
      style={{ maxWidth: maxWidthPx }}
      className={cn(
        'w-56 cursor-pointer text-left text-sm text-lynch-400 transition-colors',
        'hover:text-lynch-600 focus:outline-none focus-visible:text-lynch-600',
        'dark:text-lynch-500 dark:hover:text-lynch-300',
      )}>
      {content}
    </div>
  );
}
